import { makeStyles } from "tss-react/mui";
import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import {
  IPrioritySchedulerColumnProps,
  PrioritySchedulerColumnEvent,
} from "../PrioritySchedulerBoard.types";
import { getCardDnDId } from "../PrioritySchedulerBoard.utils";

export interface DraggableEventCardProps {
  event: PrioritySchedulerColumnEvent;
  layout: { top: number; height: number };
  columnId: string;
  onEventRemove?: (entityId: string) => void;
  onCardClick: IPrioritySchedulerColumnProps["onCardClick"];
  EventComponent: IPrioritySchedulerColumnProps["EventComponent"];
}

export function DraggableEventCard({
  event,
  layout,
  columnId,
  onEventRemove,
  onCardClick,
  EventComponent,
}: Readonly<DraggableEventCardProps>) {
  const { classes, cx } = useStyles({
    top: layout.top,
    height: layout.height,
  });
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getCardDnDId(event.id),
    data: {
      type: "card",
      cardId: event.id,
      container: { type: "column", id: columnId },
    },
  });

  return (
    <Box
      ref={setNodeRef}
      className={cx(
        classes.eventCard,
        event?.isGhost && classes.ghostEvent,
        isDragging && classes.draggingEvent
      )}
      {...attributes}
      {...listeners}
    >
      {EventComponent && (
        <EventComponent
          attempt={event}
          onCardClick={onCardClick}
          onEventRemove={
            onEventRemove ? () => onEventRemove(event.id) : undefined
          }
          isRemovable={!event.assignedUserId}
          className={classes.eventContainer}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles<{ top: number; height: number }>()(
  (theme, { top, height }) => ({
    eventCard: {
      position: "absolute",
      top,
      left: theme.spacing(1),
      right: theme.spacing(1),
      height,
      zIndex: 1,
    },
    ghostEvent: {
      opacity: 0.5,
    },
    eventContainer: {
      width: "100%",
    },
    draggingEvent: {
      opacity: 0.45,
    },
  })
);
